import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  Stack
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useFilterContext } from './filtercontext';
import { useRetainedSearchInput, renderSelectionTags } from './multiselectshared';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Module-level label default: an inline default in the destructure would be a
// new function every render and invalidate the options memos (render-loop
// hazard, see the filter-component-stability skill).
const defaultGetLabel = value => String(value);

export default function DependentMultiSelectPairFilter({
  primaryField,
  secondaryField,
  primaryLabel,
  secondaryLabel,
  label, // Overall filter label
  getPrimaryValue: getPrimaryValueProp,
  getSecondaryValue: getSecondaryValueProp,
  getPrimaryLabel: getPrimaryLabelProp,
  getSecondaryLabel: getSecondaryLabelProp,
  // Optional seeds (arrays of values) for URL-driven drill-downs. When
  // provided, we treat the user as having already interacted so the
  // auto-select-all effects below don't clobber the seeded selections once
  // originalData arrives.
  initialPrimary = null,
  initialSecondary = null,
}) {
  const { originalData, registerFilter } = useFilterContext();

  // Value getters close over the field names, so stabilize them with
  // useCallback keyed on the field (same loop hazard as defaultGetLabel).
  const getPrimaryValue = useCallback(
    getPrimaryValueProp || (item => item[primaryField]),
    [getPrimaryValueProp, primaryField],
  );
  const getSecondaryValue = useCallback(
    getSecondaryValueProp || (item => item[secondaryField]),
    [getSecondaryValueProp, secondaryField],
  );
  const getPrimaryLabel = getPrimaryLabelProp || defaultGetLabel;
  const getSecondaryLabel = getSecondaryLabelProp || defaultGetLabel;

  const hasInitialSeed = Boolean(
    (Array.isArray(initialPrimary) && initialPrimary.length > 0) ||
    (Array.isArray(initialSecondary) && initialSecondary.length > 0)
  );

  const [primarySelected, setPrimarySelected] = useState(() =>
    Array.isArray(initialPrimary) ? [...initialPrimary] : []
  );
  const [secondarySelected, setSecondarySelected] = useState(() =>
    Array.isArray(initialSecondary) ? [...initialSecondary] : []
  );
  // Treat seeded mounts as already-interacted so the "select all on first
  // data load" effects don't overwrite the seed.
  const [hasUserInteracted, setHasUserInteracted] = useState(hasInitialSeed);

  // Memoize primary options calculation
  const primaryOptions = useMemo(() => {
    if (!originalData || !originalData.length) return [];

    const valueSet = new Set();
    originalData.forEach(item => {
      const value = getPrimaryValue(item);
      if (value !== undefined && value !== null) {
        valueSet.add(value);
      }
    });

    return Array.from(valueSet).sort((a, b) => {
      const labelA = getPrimaryLabel(a).toLowerCase();
      const labelB = getPrimaryLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [originalData, getPrimaryValue, getPrimaryLabel]);

  // Memoize secondary options based on primary selection
  const secondaryOptions = useMemo(() => {
    if (!originalData || !originalData.length || primarySelected.length === 0) return [];

    const valueSet = new Set();
    const primarySelectedSet = new Set(primarySelected);

    originalData.forEach(item => {
      const primaryValue = getPrimaryValue(item);
      if (primarySelectedSet.has(primaryValue)) {
        const secondaryValue = getSecondaryValue(item);
        if (secondaryValue !== undefined && secondaryValue !== null) {
          valueSet.add(secondaryValue);
        }
      }
    });

    return Array.from(valueSet).sort((a, b) => {
      const labelA = getSecondaryLabel(a).toLowerCase();
      const labelB = getSecondaryLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [originalData, primarySelected, getPrimaryValue, getSecondaryValue, getSecondaryLabel]);

  // Memoize the clear handler to prevent recreation on every render. Clearing
  // returns the filter to untouched, so later-arriving values are selected too.
  const handleClearFromContext = useCallback(() => {
    setPrimarySelected([...primaryOptions]);
    setSecondarySelected([...secondaryOptions]);
    setHasUserInteracted(false);
  }, [primaryOptions, secondaryOptions]);

  // Select all options until the user touches the filter — including values
  // that arrive in a later load batch (progressive drilldowns), which a
  // first-load-only select-all silently excluded (Trello ZE03rv0c). Untouched,
  // the selection is always the whole option list, so a length mismatch means
  // new options arrived; the length guard stops the effect re-firing.
  useEffect(() => {
    if (primaryOptions.length && primarySelected.length !== primaryOptions.length && !hasUserInteracted) {
      setPrimarySelected([...primaryOptions]);
    }
  }, [primaryOptions, primarySelected.length, hasUserInteracted]);

  useEffect(() => {
    if (secondaryOptions.length && secondarySelected.length !== secondaryOptions.length && !hasUserInteracted && primarySelected.length > 0) {
      setSecondarySelected([...secondaryOptions]);
    }
  }, [secondaryOptions, secondarySelected.length, hasUserInteracted, primarySelected.length]);

  // Clear invalid secondary selections when primary changes.
  // IMPORTANT: skip while secondaryOptions hasn't populated yet — otherwise
  // a URL-seeded selection (e.g., from a DRFA drill-down) gets wiped on
  // mount before originalData arrives, since no value is "valid" against
  // an empty options list. Once options has at least one entry, the
  // validation runs normally.
  useEffect(() => {
    if (hasUserInteracted && secondarySelected.length > 0 && secondaryOptions.length > 0) {
      const validSecondaryValues = secondarySelected.filter(value =>
        secondaryOptions.includes(value)
      );

      if (validSecondaryValues.length !== secondarySelected.length) {
        setSecondarySelected(validSecondaryValues);
      }
    }
  }, [secondaryOptions, secondarySelected, hasUserInteracted]);

  // Register filter with the context - modeled after working MultiSelectFilter
  useEffect(() => {
    const primaryCount = primarySelected.length;
    const secondaryCount = secondarySelected.length;
    const totalPrimaryCount = primaryOptions.length;
    const totalSecondaryCount = secondaryOptions.length;

    let filterFn;
    let filterDescription;

    if (primaryCount === 0) {
      // No primary selections = show nothing
      filterFn = () => false;
      filterDescription = `${label}: No Primary Values`;
    } else if (primaryCount === totalPrimaryCount && secondaryCount === totalSecondaryCount) {
      // All items selected = show everything. No description: registered but
      // narrowing nothing (see describeActiveFilters in filtercontext).
      filterFn = () => true;
      filterDescription = null;
    } else {
      // Some filtering needed
      const primarySelectedSet = new Set(primarySelected);
      const secondarySelectedSet = new Set(secondarySelected);

      if (secondaryCount === 0) {
        // Primary selected but no secondary = filter by primary only
        filterFn = item => {
          const itemPrimaryValue = getPrimaryValue(item);
          return primarySelectedSet.has(itemPrimaryValue);
        };

        if (primaryCount >= 5) {
          filterDescription = `${label}: ${primaryCount} primary selected`;
        } else {
          filterDescription = `${label}: ${primarySelected.map(getPrimaryLabel).join(', ')}`;
        }
      } else {
        // Both primary and secondary selected = filter by both
        filterFn = item => {
          const itemPrimaryValue = getPrimaryValue(item);
          const itemSecondaryValue = getSecondaryValue(item);
          return primarySelectedSet.has(itemPrimaryValue) && secondarySelectedSet.has(itemSecondaryValue);
        };

        const primaryDesc = primaryCount >= 3 ? `${primaryCount} primary` : primarySelected.map(getPrimaryLabel).join(', ');
        const secondaryDesc = secondaryCount >= 3 ? `${secondaryCount} secondary` : secondarySelected.map(getSecondaryLabel).join(', ');
        filterDescription = `${label}: ${primaryDesc} → ${secondaryDesc}`;
      }
    }

    const descriptionFn = () => filterDescription;
    registerFilter(
      `${primaryField}_${secondaryField}`,
      label,
      filterFn,
      handleClearFromContext,
      descriptionFn
    );
  }, [
    primarySelected,
    secondarySelected,
    primaryOptions.length,
    secondaryOptions.length,
    // Remove the callback dependencies that cause re-renders
    primaryField,
    secondaryField,
    label
  ]);

  // Handle primary selection changes
  const handlePrimaryChange = useCallback((event, newValue) => {
    setHasUserInteracted(true);

    // Check if "Select All" was clicked
    if (newValue.includes('__select_all_primary__')) {
      const allCurrentlySelected = primarySelected.length === primaryOptions.length;

      if (allCurrentlySelected) {
        // Deselect all
        setPrimarySelected([]);
      } else {
        // Select all (excluding the __select_all__ marker)
        setPrimarySelected([...primaryOptions]);
      }
      return;
    }

    // Normal selection (filter out any __select_all__ markers)
    const filteredValues = (newValue || []).filter(value => value !== '__select_all_primary__');
    setPrimarySelected(filteredValues);
  }, [primarySelected.length, primaryOptions]);

  // Handle secondary selection changes
  const handleSecondaryChange = useCallback((event, newValue) => {
    setHasUserInteracted(true);

    // Check if "Select All" was clicked
    if (newValue.includes('__select_all_secondary__')) {
      const allCurrentlySelected = secondarySelected.length === secondaryOptions.length;

      if (allCurrentlySelected) {
        // Deselect all
        setSecondarySelected([]);
      } else {
        // Select all (excluding the __select_all__ marker)
        setSecondarySelected([...secondaryOptions]);
      }
      return;
    }

    // Normal selection (filter out any __select_all__ markers)
    const filteredValues = (newValue || []).filter(value => value !== '__select_all_secondary__');
    setSecondarySelected(filteredValues);
  }, [secondarySelected.length, secondaryOptions]);

  // Add "Select All" option to primary options
  const primaryOptionsWithSelectAll = useMemo(() => {
    return ['__select_all_primary__', ...primaryOptions];
  }, [primaryOptions]);

  // Add "Select All" option to secondary options
  const secondaryOptionsWithSelectAll = useMemo(() => {
    return ['__select_all_secondary__', ...secondaryOptions];
  }, [secondaryOptions]);

  // Custom option rendering for primary with checkboxes
  const renderPrimaryOption = useCallback((props, option, { selected }) => {
    const { key, ...optionProps } = props;

    // Handle "Select All" option
    if (option === '__select_all_primary__') {
      const allSelected = primarySelected.length === primaryOptions.length;
      const someSelected = primarySelected.length > 0;

      return (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            indeterminateIcon={<CheckBoxOutlineBlankIcon fontSize="small" style={{ opacity: 0.5 }} />}
            style={{ marginRight: 8 }}
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
          />
          <strong>Select All</strong>
        </li>
      );
    }

    // Regular option rendering
    return (
      <li key={key} {...optionProps}>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        {getPrimaryLabel(option)}
      </li>
    );
  }, [getPrimaryLabel, primarySelected.length, primaryOptions.length]);

  // Custom option rendering for secondary with checkboxes
  const renderSecondaryOption = useCallback((props, option, { selected }) => {
    const { key, ...optionProps } = props;

    // Handle "Select All" option
    if (option === '__select_all_secondary__') {
      const allSelected = secondarySelected.length === secondaryOptions.length;
      const someSelected = secondarySelected.length > 0;

      return (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            indeterminateIcon={<CheckBoxOutlineBlankIcon fontSize="small" style={{ opacity: 0.5 }} />}
            style={{ marginRight: 8 }}
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
          />
          <strong>Select All</strong>
        </li>
      );
    }

    // Regular option rendering
    return (
      <li key={key} {...optionProps}>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        {getSecondaryLabel(option)}
      </li>
    );
  }, [getSecondaryLabel, secondarySelected.length, secondaryOptions.length]);

  // Chips: "All (N)", a few individual values, or one "N of M selected"
  const renderPrimaryTags = useCallback((value, getTagProps) => renderSelectionTags(value, getTagProps, {
    totalCount: primaryOptions.length,
    getLabel: getPrimaryLabel,
  }), [primaryOptions.length, getPrimaryLabel]);

  const renderSecondaryTags = useCallback((value, getTagProps) => renderSelectionTags(value, getTagProps, {
    totalCount: secondaryOptions.length,
    getLabel: getSecondaryLabel,
  }), [secondaryOptions.length, getSecondaryLabel]);

  // Keep each search's text while toggling options (MUI clears it on select)
  const primarySearch = useRetainedSearchInput();
  const secondarySearch = useRetainedSearchInput();

  // Custom input rendering for primary
  const renderPrimaryInput = useCallback((params) => (
    <TextField
      {...params}
      label={primaryLabel}
      size="small"
      placeholder="Search and select..."
    />
  ), [primaryLabel]);

  // Custom input rendering for secondary
  const renderSecondaryInput = useCallback((params) => (
    <TextField
      {...params}
      label={secondaryLabel}
      size="small"
      placeholder="Search and select..."
      disabled={primarySelected.length === 0}
    />
  ), [secondaryLabel, primarySelected.length]);

  // Custom getOptionLabel for primary autocomplete
  const getPrimaryOptionLabel = useCallback((option) => {
    if (option === '__select_all_primary__') {
      return 'Select All';
    }
    return getPrimaryLabel(option);
  }, [getPrimaryLabel]);

  // Custom getOptionLabel for secondary autocomplete
  const getSecondaryOptionLabel = useCallback((option) => {
    if (option === '__select_all_secondary__') {
      return 'Select All';
    }
    return getSecondaryLabel(option);
  }, [getSecondaryLabel]);

  // Custom filtering for primary to exclude "Select All" from search results
  const filterPrimaryOptions = useCallback((options, { inputValue }) => {
    const filtered = options.filter(option => {
      if (option === '__select_all_primary__') {
        return true; // Always show "Select All"
      }
      return getPrimaryLabel(option).toLowerCase().includes(inputValue.toLowerCase());
    });
    return filtered;
  }, [getPrimaryLabel]);

  // Custom filtering for secondary to exclude "Select All" from search results
  const filterSecondaryOptions = useCallback((options, { inputValue }) => {
    const filtered = options.filter(option => {
      if (option === '__select_all_secondary__') {
        return true; // Always show "Select All"
      }
      return getSecondaryLabel(option).toLowerCase().includes(inputValue.toLowerCase());
    });
    return filtered;
  }, [getSecondaryLabel]);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Stack spacing={2}>
        {/* Primary Autocomplete */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {primaryLabel}
            {primaryOptions.length > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({primarySelected.length}/{primaryOptions.length})
              </Typography>
            )}
          </Typography>

          <Autocomplete
            multiple
            options={primaryOptionsWithSelectAll}
            value={primarySelected}
            limitTags={5}
            onChange={handlePrimaryChange}
            inputValue={primarySearch.inputValue}
            onInputChange={primarySearch.onInputChange}
            onClose={primarySearch.onClose}
            disableCloseOnSelect
            getOptionLabel={getPrimaryOptionLabel}
            filterOptions={filterPrimaryOptions}
            renderOption={renderPrimaryOption}
            renderTags={renderPrimaryTags}
            renderInput={renderPrimaryInput}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            fullWidth
          />
        </Box>

        {/* Secondary Autocomplete */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {secondaryLabel}
            {secondaryOptions.length > 0 && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({secondarySelected.length}/{secondaryOptions.length})
              </Typography>
            )}
          </Typography>

          <Autocomplete
            multiple
            options={secondaryOptionsWithSelectAll}
            value={secondarySelected}
            limitTags={5}
            onChange={handleSecondaryChange}
            inputValue={secondarySearch.inputValue}
            onInputChange={secondarySearch.onInputChange}
            onClose={secondarySearch.onClose}
            disableCloseOnSelect
            getOptionLabel={getSecondaryOptionLabel}
            filterOptions={filterSecondaryOptions}
            renderOption={renderSecondaryOption}
            renderTags={renderSecondaryTags}
            renderInput={renderSecondaryInput}
            isOptionEqualToValue={(option, value) => option === value}
            size="small"
            fullWidth
            disabled={primarySelected.length === 0}
            key={primarySelected.join(',')} // Force re-render when primary changes
          />
        </Box>
      </Stack>
    </Box>
  );
}